import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Unstable_Grid2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";

import { getPointsClass } from "../../utils/GroupAggregator";

import Bar from "../../charts/Bar";
import BarChart from "../../charts/BarChart";
import AssessmentItem from "./AssessmentItem";
import Question from "../../form/Question";
import Guidance from "../../form/Guidance";
import { getSummary } from "../context/AssessmentContext";
import EventBus from "../../utils/EventBus";

export default function AssessmentCategoryForm(
  props = { categoryModel: undefined },
) {
  const {
    progressItemsList,
    defaultAssessmentType,
    refreshItemList,
    assessment,
    categoryModel,
  } = props;

  const category = categoryModel;

  const TitleBar = memo(function TitleBar({
    id,
    title,
    earnedPoints = undefined,
    maxPoints = undefined,
    findingRating = NaN,
  }) {
    return (
      <Grid container spacing={0} xs={12} md={12}>
        <Grid xs={4} style={{ paddingLeft: 0 }}>
          <Typography>
            {id}. <span>{title}</span>
          </Typography>
        </Grid>

        <Grid xs={2.5}>
          <Typography>
            Earned Points: {earnedPoints === undefined ? "--" : earnedPoints}
          </Typography>
        </Grid>

        <Grid xs={2.5}>
          <Typography>
            Max Points: {maxPoints === undefined ? "--" : maxPoints}
          </Typography>
        </Grid>

        <Grid xs={3}>
          <Grid xs={6}>
            <Typography>
              Finding Rating,{" "}
              {isNaN(findingRating) ? "--" : `${findingRating}%`}
            </Typography>
          </Grid>

          <Grid xs={6}>
            <BarChart slim>
              <Bar
                value={findingRating}
                className={getPointsClass(findingRating)}
              />
            </BarChart>
          </Grid>
        </Grid>
      </Grid>
    );
  });

  const CategoryLayout = memo(function CategoryLayout({ category }) {
    const [itemsList, setItemsList] = useState(progressItemsList);

    const myRef = useRef(null);

    const [expanded, setExpanded] = useState(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
      const progressUpdate = (_itemsList) => {
        setItemsList(_itemsList);
      };

      EventBus.on("PROGRESS", progressUpdate);

      return () => {
        if (EventBus.off) {
          EventBus.off("PROGRESS", progressUpdate);
        }
      };
    }, []);

    useEffect(() => {
      setItemsList(progressItemsList);
    }, [progressItemsList]);

    const collapse = () => {
      setExpanded(false);

      if (myRef.current && myRef.current.getBoundingClientRect) {
        let pos = myRef.current.getBoundingClientRect();

        window.scrollTo({
          top: pos.top + window.scrollY,
          left: 0,
          behavior: "smooth",
        });
      }
    };

    const toggle = () => {
      setInitialized(true);
      refreshItemList?.();
      setExpanded((prev) => !prev);
    };

    const summary = useMemo(() => {
      return getSummary(itemsList, category?.props?.id);
    }, [category, itemsList]);

    console.log("CATEGORY => ", category);

    return (
      <Card className="tis-quality-and-fs-category" sx={{ width: "100%" }}>
        <CardHeader
          ref={myRef}
          onClick={toggle}
          action={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          title={
            <TitleBar
              id={category?.props?.id}
              title={category?.props?.title}
              earnedPoints={summary?.earnedPoints || 0}
              maxPoints={summary?.maxPoints || 0}
              findingRating={summary?.findingRating || 0}
            />
          }
        />

        <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
          {initialized && (
            <CardContent className="tis-quality-and-fs-category-content">
              {category?.children?.map((section, idx) => (
                <SectionLayout key={idx} section={section} />
              ))}

              <div className="tis-collapse" onClick={collapse}>
                Collapse {category?.props?.title} <ExpandLessIcon />
              </div>
            </CardContent>
          )}
        </Collapse>
      </Card>
    );
  });

  const SectionLayout = memo(function SectionLayout({ section }) {
    const [itemsList, setItemsList] = useState(progressItemsList);

    const myRef = useRef(null);

    const [expanded, setExpanded] = useState(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
      const progressUpdate = (_itemsList) => {
        setItemsList(_itemsList);
      };

      EventBus.on("PROGRESS", progressUpdate);

      return () => {
        if (EventBus.off) {
          EventBus.off("PROGRESS", progressUpdate);
        }
      };
    }, []);

    useEffect(() => {
      setItemsList(progressItemsList);
    }, [progressItemsList]);

    const collapse = () => {
      setExpanded(false);

      if (myRef.current && myRef.current.getBoundingClientRect) {
        let pos = myRef.current.getBoundingClientRect();

        window.scrollTo({
          top: pos.top + window.scrollY,
          left: 0,
          behavior: "smooth",
        });
      }
    };

    const toggle = () => {
      setInitialized(true);
      setExpanded((prev) => !prev);
    };

    const itemComponentMap = {
      Question: Question,
      Guidance: Guidance,
    };

    const itemInclude = (list = []) => {
      return list.map((comp) => {
        const CompType = itemComponentMap[comp.type];

        if (!CompType) return null;

        return (
          <CompType
            key={comp?.props?.id}
            assessment={assessment}
            {...comp.props}
          >
            {comp.children && itemInclude(comp.children)}
          </CompType>
        );
      });
    };

    const summary = useMemo(() => {
      return getSummary(itemsList, section?.props?.id);
    }, [itemsList, section]);

    console.log("SECTION => ", section);

    return (
      <>
        {(section?.props?.visible === undefined ||
          section?.props?.visible === true) && (
          <Card className="tis-quality-and-fs-section" sx={{ width: "100%" }}>
            <CardHeader
              ref={myRef}
              onClick={toggle}
              action={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              title={
                <TitleBar
                  id={section?.props?.id}
                  title={section?.props?.title}
                  earnedPoints={summary?.earnedPoints}
                  maxPoints={summary?.maxPoints}
                  findingRating={summary?.findingRating}
                />
              }
            />

            <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
              {initialized && (
                <CardContent>
                  {section?.children?.map((item) => {
                    return (
                      <AssessmentItem
                        key={item?.props?.id}
                        assessment={assessment}
                        {...item.props}
                        onChangePoints={() => {}}
                        refreshItemList={refreshItemList}
                        defaultAssessmentType={defaultAssessmentType}
                      >
                        {item.children && itemInclude(item.children)}
                      </AssessmentItem>
                    );
                  })}

                  <div className="tis-collapse" onClick={collapse}>
                    Collapse {section?.props?.title} <ExpandLessIcon />
                  </div>
                </CardContent>
              )}
            </Collapse>
          </Card>
        )}

        {section?.props?.visible === false && (
          <div>
            {section?.children?.map((item) => {
              return (
                <AssessmentItem
                  key={item?.props?.id}
                  assessment={assessment}
                  {...item.props}
                  onChangePoints={() => {}}
                  refreshItemList={refreshItemList}
                  defaultAssessmentType={defaultAssessmentType}
                >
                  {item.children && itemInclude(item.children)}
                </AssessmentItem>
              );
            })}
          </div>
        )}
      </>
    );
  });

  return <CategoryLayout category={category} />;
}
